// TeamNotes SDK — lets the app's logged-in end user (WorkflowUser) send a
// feedback note (optionally with an annotated screenshot) to the app builder.
// Gated by the workflow's Team Notes setting; notes surface in the builder's
// Notes tab. Browser-only: notes are user-initiated UI feedback, so the Deno
// copy of this file is deliberately inert (no dual runtime like CustomAuditLog).
const isBrowser = typeof window !== "undefined" && typeof document !== "undefined";

// Resolve the API host from the current hostname — matches User.js so local /
// staging / prod all hit the right backend (the config keys aren't populated
// in local builds, which is why the hardcoded default leaked to prod).
function getApiUrl() {
    if (!isBrowser) return null;
    const hostname = window?.location?.hostname ?? "";
    if (hostname === "localhost" || hostname.includes("agentui.test")) {
        return "http://localhost:3333";
    }
    const isStaging =
        hostname.includes("staging-apps.agentui.ai") ||
        hostname.includes("staging-api.agentui.ai") ||
        hostname === "staging.agentui.ai";
    if (isStaging) return "https://staging-api.agentui.ai";
    return "https://api.agentui.ai";
}

function getWorkflowId() {
    return isBrowser ? window.config?.workflowId || null : null;
}

function getAuthToken() {
    const workflowId = getWorkflowId();
    if (!isBrowser || !workflowId) return null;
    try {
        return localStorage.getItem(`workflow-auth-${workflowId}`);
    } catch {
        return null;
    }
}

class TeamNotesError extends Error {
    constructor(message, code) {
        super(message);
        this.name = "TeamNotesError";
        this.code = code; // "disabled"|"login-required"|"too-large"|"rate-limited"|"full"|"invalid"|"network"
    }
}

const CODE_BY_STATUS = {
    401: "login-required",
    403: "disabled",
    409: "full",
    413: "too-large",
    429: "rate-limited",
};

export const TeamNotes = {
    /** True when the feature is on AND the user holds a workflow auth token. */
    isEnabled() {
        return Boolean(
            isBrowser && window.config?.teamNotesEnabled && getAuthToken()
        );
    },

    /**
     * Send a note to the app builder. Screenshots must be uploaded first (via
     * the standard upload library) and passed as a File id — no image bytes go
     * through this call.
     * @param {{text: string, screenshotFileId?: string|null}} params
     * @returns {Promise<{id: string, createdAt: string, hasScreenshot: boolean}>}
     * @throws {TeamNotesError} typed by code for UI-friendly handling
     */
    async create({ text, screenshotFileId = null } = {}) {
        if (!isBrowser) {
            throw new TeamNotesError("TeamNotes is browser-only", "invalid");
        }
        const workflowId = getWorkflowId();
        const token = getAuthToken();
        if (!workflowId) {
            throw new TeamNotesError("No workflow context", "invalid");
        }
        if (!token) {
            throw new TeamNotesError(
                "You must be logged in to leave a note",
                "login-required"
            );
        }

        let response;
        try {
            response = await fetch(
                `${getApiUrl()}/workflows/${workflowId}/auth/team-notes`,
                {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${token}`,
                    },
                    body: JSON.stringify({ text, screenshotFileId }),
                }
            );
        } catch (error) {
            throw new TeamNotesError(
                "Could not reach the server — check your connection and try again",
                "network"
            );
        }

        const body = await response.json().catch(() => ({}));
        if (!response.ok) {
            // Coerce to a string: some error bodies carry `error` as an object
            // (or the endpoint isn't live yet and returns a non-standard shape),
            // which would otherwise render as "[object Object]".
            const serverError =
                typeof body?.error === "string" ? body.error : null;
            throw new TeamNotesError(
                serverError || `Failed to send note (${response.status})`,
                CODE_BY_STATUS[response.status] || "invalid"
            );
        }
        return body?.data || body;
    },
};

export default TeamNotes;
